import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';

import { OfferSource } from '@cds';
import { FixtureType } from '@cds/betting-offer';
import { DayOfRaceDate } from '@cds/betting-offer/domain-specific/horse-racing';
import { OfferMapping } from '@cds/query-objects';
import { BettingOfferApi } from '@frontend/sports/betting-offer/feature/offer-service';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { RouteTag } from '@frontend/sports/common/core/data-access/route';
import { RacingUrlParam } from '@frontend/sports/racing/feature/models';
import { Observable, firstValueFrom, from } from 'rxjs';

import { GreyhoundRacingApiService } from '../../../../horse-racing-shared/services/greyhound-racing-api.service';
import { AdaptiveLayoutState } from '../../../../layout/adaptive-layout.service';
import { UrlHelperService } from '../../../../navigation-core/url-helper.service';
import { SubNavigationResolve } from '../../../../sub-navigation/sub-navigation-resolve.service';
import { SubNavigationItem } from '../../../../sub-navigation/sub-navigation.models';

enum GreyhoundTabType {
    NextRace = 1,
    Today,
    Tomorrow,
    Future,
}

@Injectable()
export class GreyhoundSubNavigationService extends SubNavigationResolve<any> {
    fixture: any;
    buildSubNavigation(layout: AdaptiveLayoutState, routeSnapshot: ActivatedRouteSnapshot): void {
        const routeTag = routeSnapshot.data?.tag ? routeSnapshot.data.tag : null;
        this.getDefaultTabsGreyhounds(routeTag).subscribe((routes) => this.tabs$.next(routes));
        this.size$.next('small');
        this.fixture = routeSnapshot.params.fixture;
    }

    isTodayAvailable = true;
    isTomorrowRacesAvailable = true;
    isNextRacesAvailable = true;
    isFutureRaceAvailable = true;
    constructor(
        private sitecore: Sitecore,
        private urlHelper: UrlHelperService,
        private apiService: GreyhoundRacingApiService,
        private apiService1: BettingOfferApi,
    ) {
        super();
    }

    async getRaces(routeTag: RouteTag) {
        await Promise.all([this.getnextRaces(), this.getTodayRaces(), this.getTomorrowRaces(), this.getFutureRaces()]);

        return this.getDefaultTabs(routeTag);
    }

    async getnextRaces() {
        const fixtures = await firstValueFrom(this.apiService.getNextFixtures());
        this.isNextRacesAvailable = !!fixtures.length;
    }

    async getTodayRaces() {
        const response = await firstValueFrom(this.apiService.getDayOfRaceFixtures(DayOfRaceDate.Today));
        this.isTodayAvailable = !!response?.fixtures.length;
    }

    async getTomorrowRaces() {
        const response = await firstValueFrom(this.apiService.getDayOfRaceFixtures(DayOfRaceDate.Tomorrow));
        this.isTomorrowRacesAvailable = !!response?.fixtures.length;
    }

    async getFutureRaces() {
        const response = await firstValueFrom(
            this.apiService1.getFixtureList({
                fixtureIds: this.fixture,
                sportIds: '37',
                fixtureTypes: FixtureType.Antepost,
                offerSource: OfferSource.V2,
                offerMapping: OfferMapping.All,
            }),
        );
        this.isFutureRaceAvailable = !!response?.fixtures.length;
    }

    getDefaultTabsGreyhounds(routeTag: RouteTag): Observable<SubNavigationItem[]> {
        return from(this.getRaces(routeTag));
    }

    private getDefaultTabs(routeTag: any): SubNavigationItem[] {
        return [
            {
                icon: '',
                title: this.sitecore.horseRacing.navigation.NextRaces,
                url: this.urlHelper.getRaceUrl(SportConstant.Greyhounds, RacingUrlParam.NextRaces),
                active: routeTag === RouteTag.RacingRaces,
                id: GreyhoundTabType.NextRace,
                ignoreBrowserHistory: true,
                disabled: !this.isNextRacesAvailable,
            },
            {
                icon: '',
                title: this.sitecore.horseRacing.navigation.Today,
                url: this.urlHelper.getRaceUrl(SportConstant.Greyhounds, RacingUrlParam.Today),
                active: routeTag === RouteTag.RacingToday,
                id: GreyhoundTabType.Today,
                ignoreBrowserHistory: true,
                disabled: !this.isTodayAvailable,
            },
            {
                icon: '',
                title: this.sitecore.horseRacing.navigation.Tomorrow,
                url: this.urlHelper.getRaceUrl(SportConstant.Greyhounds, RacingUrlParam.Tomorrow),
                active: routeTag === RouteTag.RacingTomorrow,
                id: GreyhoundTabType.Tomorrow,
                ignoreBrowserHistory: true,
                disabled: !this.isTomorrowRacesAvailable,
            },
            {
                icon: '',
                title: this.sitecore.horseRacing.navigation.FutureAndSpecials,
                url: this.urlHelper.getRaceUrl(SportConstant.Greyhounds, RacingUrlParam.Future),
                active: routeTag === RouteTag.RacingFuture,
                id: GreyhoundTabType.Future,
                ignoreBrowserHistory: true,
                disabled: !this.isFutureRaceAvailable,
            },
        ];
    }
}
